import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NewUsersChart } from './NewUsersChart';
import { DAUChart } from './DAUChart';
import { MessageVolumeChart } from './MessageVolumeChart';
import { PostReplyVolumeChart } from './PostReplyVolumeChart';
import { PostReplyRateChart } from './PostReplyRateChart';
import { RepliesPerDayChart } from './RepliesPerDayChart';
import { LiveStatsPanel } from './LiveStatsPanel';
import { AiFriendUsageChart } from './AiFriendUsageChart';
import { PostAnalyticsChart } from './PostAnalyticsChart';
import { PostObservabilityPanel } from './PostObservabilityPanel';
import { UserOnboardingChart } from './UserOnboardingChart';

interface AllMetricsGridProps {
  className?: string;
}

/**
 * All Metrics Grid - Displays live stats and daily metrics in accordion
 * Live stats update every minute, daily charts update every hour
 */
export function AllMetricsGrid({ className }: AllMetricsGridProps) {
  const [isDailyOpen, setIsDailyOpen] = useState(true);
  const [isAiFriendOpen, setIsAiFriendOpen] = useState(true);
  const [isPostAnalyticsOpen, setIsPostAnalyticsOpen] = useState(false);

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100 tracking-[-0.03em]">
          Analytics Dashboard
        </h1>
        <p className="text-lg font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
          Real-time platform insights and historical trends
        </p>
      </div>

      {/* Live Stats Section - Always Visible */}
      <LiveStatsPanel />
      {/* Post Service Observability (series-post-service) */}
      <PostObservabilityPanel />

      {/* Daily Overview Accordion */}
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden">
        {/* Accordion Header */}
        <button
          onClick={() => setIsDailyOpen(!isDailyOpen)}
          className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                Daily Overview
              </h2>
              <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                Historical trends and daily metrics • Updates hourly
              </p>
            </div>
          </div>

          {/* Expand/Collapse Icon */}
          <motion.div
            animate={{ rotate: isDailyOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        </button>

        {/* Accordion Content */}
        <AnimatePresence>
          {isDailyOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-8 pb-8 pt-4 border-t border-gray-100 dark:border-gray-800">
                {/* 2x2 Grid of Individual Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Row 1 */}
                  <NewUsersChart />
                  <DAUChart />
                  
                  {/* Row 2 */}
                  <MessageVolumeChart />
                  <RepliesPerDayChart />

                  {/* Row 3 */}
                  <PostReplyVolumeChart />
                  <PostReplyRateChart />
                </div>

                {/* Info Footer */}
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 mt-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        About Daily Metrics
                      </h4>
                      <div className="space-y-1 text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-700 dark:text-gray-300">
                        <p><span className="font-semibold text-blue-600 dark:text-blue-400">New Users:</span> First-time user registrations on each day</p>
                        <p><span className="font-semibold text-purple-600 dark:text-purple-400">Daily Active Users (DAU):</span> Unique users who sent at least one message</p>
                        <p><span className="font-semibold text-amber-600 dark:text-amber-400">Message Volume:</span> Total messages sent across all conversations</p>
                        <p><span className="font-semibold text-indigo-600 dark:text-indigo-400">Replies per day:</span> Daily reply count (post-service observability)</p>
                        <p><span className="font-semibold text-sky-600 dark:text-sky-400">Impressions over replies:</span> Daily impressions ÷ daily replies</p>
                        <p><span className="font-semibold text-fuchsia-600 dark:text-fuchsia-400">Daily total impressions:</span> Daily impressions counter</p>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                        💡 Use the time period buttons (7d/14d/30d) on each chart to view different time ranges
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* AI Friend Usage Accordion */}
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden">

        <button
          onClick={() => setIsAiFriendOpen(!isAiFriendOpen)}
          className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                AI Friend Engagement
              </h2>
              <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                Message activity and usage rankings • Updates every 5 minutes
              </p>
            </div>
          </div>


          <motion.div
            animate={{ rotate: isAiFriendOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        </button>

        <AnimatePresence>
          {isAiFriendOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-8 pb-8 pt-4 border-t border-gray-100 dark:border-gray-800">
                <AiFriendUsageChart />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Post Analytics Accordion
      <PostAnalyticsChart /> */}

      {/* Post Service Observability (series-post-service) */}
      {/* User Onboarding Conversion Accordion */}
      <UserOnboardingChart />
    </div>
  );
}
